import { useCallback, useState } from "react";
import { useToast } from "@chakra-ui/react";
import {
  useDeleteEvidence,
  useGetEvidenceDownloadUrl,
  useListEvidence,
  useUploadEvidence,
} from "state/members/accessReviewsEvidence";
import EvidencePanel, {
  type EvidenceUpload,
} from "../AccessReviews/campaigns/components/EvidencePanel";
import { EVIDENCE_BODY } from "./evidenceCopy";

const MAX_SIZE_BYTES = 4 * 1024 * 1024;

/**
 * The review-level evidence panel, embedded on an owner sign-off attestation
 * (PLO-5185). Same endpoints and same panel as the review page; the Luna
 * "analyse" action is per account-set and is not offered here.
 */
const AttestationEvidence = ({
  reviewId,
  readOnly,
}: {
  reviewId: string;
  readOnly: boolean;
}) => {
  const toast = useToast();
  const { evidenceFiles, refetch } = useListEvidence(reviewId);
  const uploadEvidence = useUploadEvidence();
  const deleteEvidence = useDeleteEvidence();
  const getDownloadUrl = useGetEvidenceDownloadUrl();
  const [uploads, setUploads] = useState<EvidenceUpload[]>([]);

  const uploadFile = useCallback(
    async (item: EvidenceUpload) => {
      try {
        await uploadEvidence.mutateAsync({ reviewId, file: item.file });
        setUploads((prev) => prev.filter((u) => u.id !== item.id));
        refetch();
      } catch (error) {
        setUploads((prev) =>
          prev.map((u) =>
            u.id === item.id
              ? {
                  ...u,
                  status: "error",
                  error:
                    error instanceof Error ? error.message : "Upload failed",
                }
              : u,
          ),
        );
      }
    },
    [reviewId, uploadEvidence, refetch],
  );

  const handleUpload = useCallback(
    (fileList: File[]) => {
      const valid = fileList.filter((f) => {
        if (f.size > MAX_SIZE_BYTES) {
          toast({
            title: "File too large",
            description: `${f.name} is larger than 4MB`,
            status: "error",
            duration: 3000,
            isClosable: true,
          });
          return false;
        }
        return true;
      });
      if (valid.length === 0) return;

      const items: EvidenceUpload[] = valid.map((file, i) => ({
        id: `${Date.now()}-${i}-${file.name}`,
        name: file.name,
        size: file.size,
        status: "uploading",
        file,
      }));
      setUploads((prev) => [...prev, ...items]);
      items.forEach((item) => void uploadFile(item));
    },
    [toast, uploadFile],
  );

  const handleRetry = useCallback(
    (id: string) => {
      setUploads((prev) => {
        const target = prev.find((u) => u.id === id);
        if (target) void uploadFile({ ...target, status: "uploading" });
        return prev.map((u) =>
          u.id === id ? { ...u, status: "uploading", error: undefined } : u,
        );
      });
    },
    [uploadFile],
  );

  const handleDismiss = useCallback(
    (id: string) => setUploads((prev) => prev.filter((u) => u.id !== id)),
    [],
  );

  const handleDownload = useCallback(
    async (fileId: string) => {
      try {
        const response = await getDownloadUrl.mutateAsync({ reviewId, fileId });
        if (response?.data?.download_url) {
          window.open(response.data.download_url, "_blank");
        }
      } catch (error) {
        toast({
          title: "Download failed",
          description: "Failed to get download URL",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    },
    [getDownloadUrl, reviewId, toast],
  );

  const handleDelete = useCallback(
    async (fileId: string, filename: string) => {
      try {
        await deleteEvidence.mutateAsync({ reviewId, fileId });
        refetch();
        toast({
          title: "Evidence deleted",
          description: `${filename} has been removed`,
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      } catch (error) {
        toast({
          title: "Delete failed",
          description: "Failed to delete evidence file",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    },
    [deleteEvidence, refetch, reviewId, toast],
  );

  if (readOnly && evidenceFiles.length === 0) return null;

  return (
    <EvidencePanel
      files={evidenceFiles}
      uploads={uploads}
      isDownloading={getDownloadUrl.isLoading}
      isDeleting={deleteEvidence.isLoading}
      canAnalyse={false}
      hasExistingAnalysis={false}
      readOnly={readOnly}
      description={EVIDENCE_BODY}
      showAnalyse={false}
      onUpload={handleUpload}
      onRetryUpload={handleRetry}
      onDismissUpload={handleDismiss}
      onAnalyse={() => {}}
      onDownload={handleDownload}
      onDelete={handleDelete}
    />
  );
};

export default AttestationEvidence;
